import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router';

import {
  init,
  reactRouterBrowserTracingIntegration,
  replayIntegration,
} from '@sentry/react';

init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    replayIntegration(),
  ],

  // Sentry 9 stopped inferring user IPs by default. We set this explicitly to
  // preserve the 8.x behavior we've been running on, so this SDK upgrade is not
  // also a silent change in what PII we collect.
  // TODO: pending validation with leads/compliance — if they'd rather adopt the
  // stricter 9+ default, drop this line (no other code changes needed).
  sendDefaultPii: true,

  // Thrown by the Google Maps library, not our code (ASPEN-DENTAL-18M).
  ignoreErrors: [/Could not find a 3d context/i],

  // Drop errors that originate inside the Google Maps bundle — third-party
  // and not actionable (ASPEN-DENTAL-19G / -16Q: a marker cleanup that calls
  // getRootNode on an undefined div while unmounting). We filter by source
  // URL rather than message because getRootNode is a generic DOM API that
  // React also uses, so a message-based ignore could swallow real app errors.
  denyUrls: [/maps\.googleapis\.com/, /maps-api-v3/],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 0.4,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  // TODO: Move to environment variables
  // DC-3761: without the backend origin here the browser never sends
  // `sentry-trace` to prod, so a frontend "API no response" event and the
  // backend transaction for the same request can't be joined in Sentry.
  // Glacier already allows the header in CORS_ALLOW_HEADERS.
  tracePropagationTargets: [
    /^\//,
    /^https:\/\/qa-glacier\.test-counterpart\.com/,
    ...(import.meta.env.VITE_BACKEND_URL
      ? [import.meta.env.VITE_BACKEND_URL]
      : []),
  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  environment: import.meta.env.VITE_SENTRY_ENV,
  enabled: import.meta.env.VITE_SENTRY_ENABLED === 'true',
});
